import { ShieldAlert, ShieldCheck } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Alert, AlertDescription } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Skeleton } from "@/components/ui/skeleton"
import { cn } from "@/lib/utils"
import type { CrossBrandSignals } from "@/types/candidates"

/**
 * OKS-18 — Cross-brand intelligence panel (UX Spec §Cross-brand
 * intelligence panel).
 *
 * **P0 invariant:** the rendered DOM contains ONLY:
 *   - the three numeric/boolean signals (overlap_count,
 *     previous_rejections_count, blocklist_flag)
 *   - localized labels and disclaimer text
 *
 * No brand names. No brand IDs. No brand slugs. No identifiers of any
 * kind that could leak which other brand is involved. This is enforced
 * by:
 *   1. The BE service contract — returns 3 scalars only.
 *   2. The QA Playwright negative assertion:
 *      `expect(page.locator('[data-testid*="brand"]')).toHaveCount(0)`
 *      inside `[data-testid="candidate-detail-cross-brand-panel"]`.
 *   3. The QA `cross_brand_leak_matcher.rb` regex over the full
 *      Inertia props payload.
 *
 * Any FE PR that adds a `…-brand-name` / `…-brand-id` / `…-brand-slug`
 * testid or rendered identifier is a REJECT in review (BE TRD locked).
 *
 * Locked testids:
 *   - `candidate-detail-cross-brand-panel` — Card root
 *   - `candidate-detail-cross-brand-overlap` — overlap count
 *   - `candidate-detail-cross-brand-rejections` — rejections count
 *   - `candidate-detail-cross-brand-blocklist` — blocklist badge
 *
 * OKS-163 — The overlap/rejections captions are number-aware via
 * i18next CLDR suffix-key plurals (`_one`/`_other`); each `t(...)` is
 * passed `{ count }` so the wording agrees with its count per locale
 * (EN: singular at 1; FR: singular at 0 & 1, CLDR `one` covers {0,1}).
 * The count is NOT interpolated into the caption — it stays in its own
 * `<dd>`; the count value only drives form selection.
 */
interface CrossBrandSignalsCardProps {
  signals: CrossBrandSignals
  className?: string
}

export function CrossBrandSignalsCard({
  signals,
  className,
}: CrossBrandSignalsCardProps) {
  const { t } = useTranslation()
  return (
    <Card
      data-testid="candidate-detail-cross-brand-panel"
      className={className}
    >
      <CardHeader>
        <CardTitle>
          {t("candidates.detail.cross_brand.section_title")}
        </CardTitle>
        <p className="text-muted-foreground text-xs">
          {t("candidates.detail.cross_brand.disclaimer")}
        </p>
      </CardHeader>
      <CardContent>
        <dl className="space-y-4">
          <div className="flex items-baseline justify-between gap-3">
            <dt className="text-muted-foreground text-sm">
              {t("candidates.detail.cross_brand.overlap_label")}
            </dt>
            <dd
              data-testid="candidate-detail-cross-brand-overlap"
              className="text-2xl font-semibold tabular-nums"
            >
              {signals.overlap_count}
            </dd>
          </div>
          <p className="text-muted-foreground -mt-2 text-xs">
            {t("candidates.detail.cross_brand.overlap_caption", {
              count: signals.overlap_count,
            })}
          </p>

          <div className="flex items-baseline justify-between gap-3 border-t pt-4">
            <dt className="text-muted-foreground text-sm">
              {t("candidates.detail.cross_brand.rejections_label")}
            </dt>
            <dd
              data-testid="candidate-detail-cross-brand-rejections"
              className="text-2xl font-semibold tabular-nums"
            >
              {signals.previous_rejections_count}
            </dd>
          </div>
          <p className="text-muted-foreground -mt-2 text-xs">
            {t("candidates.detail.cross_brand.rejections_caption", {
              count: signals.previous_rejections_count,
            })}
          </p>

          <div className="flex items-center justify-between gap-3 border-t pt-4">
            <dt className="text-muted-foreground text-sm">
              {t("candidates.detail.cross_brand.blocklist_label")}
            </dt>
            <dd>
              <BlocklistBadge flag={signals.blocklist_flag} />
            </dd>
          </div>
        </dl>
      </CardContent>
    </Card>
  )
}

function BlocklistBadge({ flag }: { flag: boolean }) {
  const { t } = useTranslation()
  if (flag) {
    return (
      <Badge
        variant="outline"
        data-testid="candidate-detail-cross-brand-blocklist"
        data-state="on"
        className="gap-1.5 border-red-300 bg-red-100 text-red-900 dark:bg-red-900/30 dark:text-red-200"
      >
        <ShieldAlert className="size-3" aria-hidden="true" />
        <span>{t("candidates.detail.cross_brand.blocklist_on")}</span>
      </Badge>
    )
  }
  return (
    <Badge
      variant="outline"
      data-testid="candidate-detail-cross-brand-blocklist"
      data-state="clear"
      className="gap-1.5 border-emerald-300 bg-emerald-100 text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-200"
    >
      <ShieldCheck className="size-3" aria-hidden="true" />
      <span>{t("candidates.detail.cross_brand.blocklist_clear")}</span>
    </Badge>
  )
}

/**
 * Loading skeleton — kept here in the same file so the deferred-prop
 * fallback on the page is a one-import affordance. Sticky until the
 * deferred prop hydrates, even after the rest of the page renders
 * (UX Spec §Loading + §Cross-brand panel skeleton ordering).
 */
export function CrossBrandSignalsSkeleton({
  className,
}: {
  className?: string
}) {
  const { t } = useTranslation()
  return (
    <Card
      data-testid="candidate-detail-cross-brand-loading"
      role="status"
      aria-label={t("candidates.detail.cross_brand.loading_aria")}
      className={cn(className)}
    >
      <CardHeader>
        <Skeleton className="h-5 w-48" />
        <Skeleton className="h-3 w-64" />
      </CardHeader>
      <CardContent className="space-y-4">
        {[0, 1].map((i) => (
          <div key={i} className="flex items-baseline justify-between gap-3">
            <Skeleton className="h-3 w-32" />
            <Skeleton className="h-7 w-12" />
          </div>
        ))}
        <div className="flex items-center justify-between gap-3 border-t pt-4">
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-6 w-20 rounded-full" />
        </div>
      </CardContent>
    </Card>
  )
}

/**
 * Inline error sub-panel — does NOT block the rest of the detail page.
 * Renders an alert + retry button; retry triggers Inertia partial
 * reload of just the deferred prop.
 */
interface CrossBrandSignalsErrorProps {
  onRetry: () => void
  className?: string
}

export function CrossBrandSignalsError({
  onRetry,
  className,
}: CrossBrandSignalsErrorProps) {
  const { t } = useTranslation()
  return (
    <Alert
      variant="destructive"
      data-testid="candidate-detail-cross-brand-error"
      className={cn(
        "flex flex-col gap-2 sm:flex-row sm:items-center",
        className,
      )}
    >
      <AlertDescription className="flex-1">
        {t("candidates.detail.cross_brand.error")}
      </AlertDescription>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={onRetry}
        data-testid="candidate-detail-cross-brand-retry"
      >
        {t("candidates.detail.cross_brand.retry")}
      </Button>
    </Alert>
  )
}
